/**
 * A browser for the directories and files that make up an Artifact version.
 */

import React, {useState} from 'react';

import * as Generated from '../../../generated/graphql';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {ArtifactFilePath} from './ArtifactFilePath';
import * as S from './ArtifactFiles.styles';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ArtifactFilesTree} from './ArtifactFilesTree';
import {ArtifactNodeViewer} from './ArtifactNodeViewer';

type ArtifactFilesProps = {
  // e.g. wandb-artifact://_id/4t2ku6dx9fw0tcxh3bc08zr0v9iee357jccoz4qspo35mcdamsnv0nkycl5bkxe22kg9nfozvvm0y85qc7rmjsb5b0h4ewavakl61idiysj3tosnbvms8ja4up3483og
  id: string;
  entityName: string;
  projectName: string;
  artifactType: string;
  jobName: string;
  alias: string;
};

type ArtifactFilesInnerProps = {
  // e.g. "QXJ0aWZhY3Q6Mjk3MzEzMTY1"
  id: string;
  entityName: string;
  projectName: string;
  artifactType: string;
  artifactVersionName: string;
};

export type TreeNode = {
  path: string[];
  label: string;
  folders: any;
  files: any;
  sizeBytes: number;
};

const traverseTree = (tree: TreeNode, path: string) => {
  if (path === '/') {
    return tree;
  }
  const parts = path.substring(1).split('/');
  let target = tree;
  for (const part of parts) {
    if (part in target.folders) {
      target = target.folders[part];
    } else if (part in target.files) {
      target = target.files[part];
    } else {
      throw new Error(`Could not traverse ${path}`);
    }
  }
  return target;
};

const makeTree = (files: Generated.File[]) => {
  const tree: TreeNode = {
    path: [],
    label: 'Root',
    folders: {},
    files: {},
    sizeBytes: 0,
  };
  for (const file of files) {
    const path = file.name.split('/');
    let target = tree;
    for (let i = 0; i < path.length - 1; i++) {
      const dirName = path[i];
      if (!(dirName in target.folders)) {
        const folderNode: TreeNode = {
          path: path.slice(0, i + 1),
          label: dirName,
          folders: {},
          files: {},
          sizeBytes: 0,
        };
        target.folders[dirName] = folderNode;
      }
      target.sizeBytes += file.sizeBytes;
      target = target.folders[dirName];
    }
    const filename = path[path.length - 1];
    target.files[filename] = {
      path,
      label: filename,
      artifactId: file.id,
      sizeBytes: file.sizeBytes,
    };
    target.sizeBytes += file.sizeBytes;
  }
  return tree;
};

export const ArtifactFilesInner = ({
  id,
  entityName,
  projectName,
  artifactType,
  artifactVersionName,
}: ArtifactFilesInnerProps) => {
  const [selected, setSelected] = useState('/');
  const {data, loading} = Generated.useFetchCodeArtifactFilesQuery({
    variables: {
      artifactID: id,
    },
  });
  if (loading) {
    return <Loader active inline name="artifact-files-inner-loader" />;
  }
  const fileEdges = data?.artifact?.files.edges ?? [];
  const tree = makeTree(fileEdges.map(e => e.node) as Generated.File[]);

  const onSelect = (path: string) => {
    setSelected(path);
  };
  const selectedNode = traverseTree(tree, selected);

  return (
    <S.ArtifactFilesInner>
      <S.TreePanel>
        <ArtifactFilesTree
          tree={tree}
          selected={selected}
          onSelect={onSelect}
        />
      </S.TreePanel>
      <S.FilePanel>
        <ArtifactFilePath selected={selectedNode} onSelect={onSelect} />
        <ArtifactNodeViewer
          entityName={entityName}
          projectName={projectName}
          artifactType={artifactType}
          artifactVersionName={artifactVersionName}
          node={selectedNode}
          onSelect={onSelect}
        />
      </S.FilePanel>
    </S.ArtifactFilesInner>
  );
};

export const ArtifactFiles = ({
  id,
  entityName,
  projectName,
  artifactType,
}: ArtifactFilesProps) => {
  const splitString = id.split('/');
  const artifactId = splitString[splitString.length - 1];
  const {data, loading} = Generated.useArtifactPathInfoQuery({
    variables: {artifactId},
  });

  if (loading) {
    return <Loader active inline name="artifact-files-loader" />;
  }
  const name = data?.artifact?.artifactSequence.name;
  const versionIndex = data?.artifact?.versionIndex;
  const artifactVersionName = `${name}:v${versionIndex}`;

  return (
    <ArtifactFilesInner
      id={artifactId}
      entityName={entityName}
      projectName={projectName}
      artifactType={artifactType}
      artifactVersionName={artifactVersionName}
    />
  );
};
